import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';
import { parseMarkdown, type MdSegment } from '@/lib/markdown';
import { srcSetFor } from '@/lib/images';
import { lp } from '@/client/i18n/locale';

/**
 * Minimal, dependency-free Markdown renderer for editorial article bodies.
 * Supports: `##`/`###` headings, blank-line-separated paragraphs, `- ` bullet
 * lists, inline `**bold**`, internal `[links](/logical-path)` (localized with
 * lp) and figures (`![alt](/media/… "caption")` alone in a block). Everything
 * else renders as plain text. Kept intentionally small — article copy is
 * authored in `src/content/articles.ts`. The grammar lives in `@/lib/markdown`
 * (shared with the Worker's prerender).
 */

function renderInline(segments: MdSegment[]): ReactNode[] {
  return segments.map((s, i) => {
    if (s.href) {
      const link = <Link to={lp(s.href)}>{s.text}</Link>;
      return s.bold ? <strong key={i}>{link}</strong> : <Fragment key={i}>{link}</Fragment>;
    }
    return s.bold ? <strong key={i}>{s.text}</strong> : <Fragment key={i}>{s.text}</Fragment>;
  });
}

export function Markdown({ source }: { source: string }) {
  return (
    <>
      {parseMarkdown(source).map((block, i) => {
        if (block.type === 'figure') {
          return (
            <figure key={i}>
              <img
                src={block.src}
                srcSet={srcSetFor(block.src)}
                sizes="(min-width: 900px) 640px, 100vw"
                alt={block.alt}
                loading="lazy"
                decoding="async"
              />
              {block.caption ? <figcaption>{block.caption}</figcaption> : null}
            </figure>
          );
        }
        if (block.type === 'ul') {
          return (
            <ul key={i}>
              {block.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }
        if (block.type === 'h3') return <h3 key={i}>{renderInline(block.segments)}</h3>;
        if (block.type === 'h2') return <h2 key={i}>{renderInline(block.segments)}</h2>;
        return <p key={i}>{renderInline(block.segments)}</p>;
      })}
    </>
  );
}
